import type { TunaEditor } from './plugin-types.js'

/**
 * Registry of mounted editors keyed by their root entity id, populated by
 * `element-editor` on mount/unmount.
 *
 * Surfaces OUTSIDE a document's `TunaEditorContext` — notably the agent
 * sidebar's proposal panel (`ProposalsHeader`), which is a sibling region
 * of the editor, not a descendant — cannot read the live
 * editor via `useEditor`. This registry lets them resolve the editor for a
 * proposal's target entity so Accept can actually apply the patch. When the
 * target document is not open, `getLiveEditor` returns undefined and callers
 * degrade gracefully ("open the document to apply").
 */
const liveEditors = new Map<string, TunaEditor>()

/** Register the live editor for an entity id (call on editor mount). */
export const registerLiveEditor = (entityId: string, editor: TunaEditor): void => {
  liveEditors.set(entityId, editor)
}

/**
 * Unregister on unmount. Removes the entry ONLY when it still points at this
 * editor — guards against a remount registering a newer editor for the same
 * id before the old one's cleanup runs (React StrictMode / fast nav).
 */
export const unregisterLiveEditor = (entityId: string, editor: TunaEditor): void => {
  if (liveEditors.get(entityId) === editor) {
    liveEditors.delete(entityId)
  }
}

/** The live editor for an entity id, or undefined when the doc isn't mounted. */
export const getLiveEditor = (entityId: string): TunaEditor | undefined => liveEditors.get(entityId)

/** Test-only: clear the registry between tests. */
export const resetLiveEditorRegistryForTests = (): void => {
  liveEditors.clear()
}
